import type {
	PdfExportJob,
	PdfExportJobStatus,
	PdfExportWebSocketEvent,
} from '../models/report-pdf-export.model';
import {
	PDF_EXPORT_GENERIC_ERROR,
	PDF_EXPORT_TIMEOUT_ERROR,
	PDF_EXPORT_TIMEOUT_MS,
} from '../models/report-pdf-export.model';

export interface RunReportPdfExportOptions {
	viewId: string;
	requestExport: (viewId: string) => Promise<PdfExportJob>;
	waitForJobEvent: (
		jobId: string,
		signal: AbortSignal
	) => Promise<PdfExportWebSocketEvent>;
	onJobUpdate?: (job: PdfExportJob) => void;
	timeoutMs?: number;
}

const TERMINAL_STATUSES = new Set<PdfExportJobStatus>(['READY', 'FAILED']);

export function isPdfExportWebSocketEvent(
	event: unknown
): event is PdfExportWebSocketEvent {
	if (!event || typeof event !== 'object') {
		return false;
	}
	const record = event as Record<string, unknown>;
	return (
		record['messageType'] === 'pdfExport' &&
		typeof record['jobId'] === 'string' &&
		typeof record['status'] === 'string'
	);
}

export function unwrapPdfExportSyncPayload(raw: unknown): unknown {
	if (!raw || typeof raw !== 'object') {
		return raw;
	}
	const record = raw as Record<string, unknown>;
	if ('event' in record) {
		return record['event'];
	}
	return raw;
}

export function parsePdfExportJob(row: unknown): PdfExportJob {
	if (!row || typeof row !== 'object') {
		throw new Error(PDF_EXPORT_GENERIC_ERROR);
	}
	const record = row as Record<string, unknown>;
	const jobId = record['jobId'];
	const status = record['status'];
	if (typeof jobId !== 'string' || typeof status !== 'string') {
		throw new Error(PDF_EXPORT_GENERIC_ERROR);
	}
	return {
		accountId:
			typeof record['accountId'] === 'string'
				? record['accountId']
				: null,
		completedAt:
			typeof record['completedAt'] === 'string'
				? record['completedAt']
				: null,
		createdAt:
			typeof record['createdAt'] === 'string'
				? record['createdAt']
				: null,
		downloadUrl:
			typeof record['downloadUrl'] === 'string'
				? record['downloadUrl']
				: null,
		downloadUrlExpiresAt:
			typeof record['downloadUrlExpiresAt'] === 'string'
				? record['downloadUrlExpiresAt']
				: null,
		errorMessage:
			typeof record['errorMessage'] === 'string'
				? record['errorMessage']
				: null,
		jobId,
		source:
			record['source'] === 'DOWNLOAD' ||
			record['source'] === 'EMAIL' ||
			record['source'] === 'SCHEDULED'
				? record['source']
				: null,
		status: status as PdfExportJobStatus,
		viewId: typeof record['viewId'] === 'string' ? record['viewId'] : null,
	};
}

export function pdfExportFailureMessage(
	job: Pick<PdfExportJob, 'errorMessage' | 'status'>
): string {
	if (job.status === 'FAILED' && job.errorMessage?.trim()) {
		return job.errorMessage.trim();
	}
	return PDF_EXPORT_GENERIC_ERROR;
}

export async function runReportPdfExport(
	options: RunReportPdfExportOptions
): Promise<PdfExportJob> {
	const timeoutMs = options.timeoutMs ?? PDF_EXPORT_TIMEOUT_MS;
	const initialJob = await options.requestExport(options.viewId);
	options.onJobUpdate?.(initialJob);

	if (initialJob.status === 'READY') {
		if (!initialJob.downloadUrl?.trim()) {
			throw new Error(PDF_EXPORT_GENERIC_ERROR);
		}
		return initialJob;
	}

	if (initialJob.status === 'FAILED') {
		throw new Error(pdfExportFailureMessage(initialJob));
	}

	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

	try {
		const event = await options.waitForJobEvent(
			initialJob.jobId,
			controller.signal
		);

		if (event.status === 'FAILED') {
			throw new Error(
				event.errorMessage?.trim() || PDF_EXPORT_GENERIC_ERROR
			);
		}

		if (event.status !== 'READY' || !event.downloadUrl?.trim()) {
			throw new Error(PDF_EXPORT_GENERIC_ERROR);
		}

		const completedJob: PdfExportJob = {
			...initialJob,
			status: 'READY',
			downloadUrl: event.downloadUrl,
			errorMessage: event.errorMessage ?? null,
		};
		options.onJobUpdate?.(completedJob);
		return completedJob;
	} catch (error) {
		if (controller.signal.aborted) {
			throw new Error(PDF_EXPORT_TIMEOUT_ERROR);
		}
		throw error;
	} finally {
		clearTimeout(timeoutId);
	}
}

export function resolvePdfDownloadFileName(
	downloadUrl: string,
	preferredName?: string
): string {
	const trimmedName = preferredName?.trim();
	if (trimmedName) {
		return trimmedName.toLowerCase().endsWith('.pdf')
			? trimmedName
			: `${trimmedName}.pdf`;
	}

	try {
		const segment = new URL(downloadUrl).pathname.split('/').pop();
		if (segment?.toLowerCase().endsWith('.pdf')) {
			return decodeURIComponent(segment);
		}
	} catch {
		// Ignore malformed URLs and fall back to the default name.
	}

	return 'report.pdf';
}

function triggerBlobDownload(objectUrl: string, fileName: string): void {
	const anchor = document.createElement('a');
	anchor.href = objectUrl;
	anchor.download = fileName;
	anchor.rel = 'noopener';
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
}

function triggerHiddenFrameDownload(url: string): void {
	const iframe = document.createElement('iframe');
	iframe.style.display = 'none';
	iframe.src = url;
	document.body.appendChild(iframe);
	window.setTimeout(() => iframe.remove(), 120_000);
}

export async function triggerPdfDownload(
	downloadUrl: string,
	fileName?: string
): Promise<void> {
	const url = downloadUrl.trim();
	if (!url) {
		throw new Error(PDF_EXPORT_GENERIC_ERROR);
	}

	const resolvedName = resolvePdfDownloadFileName(url, fileName);

	try {
		const response = await fetch(url, {
			mode: 'cors',
			credentials: 'omit',
		});
		if (!response.ok) {
			throw new Error('fetch failed');
		}

		const blob = await response.blob();
		const objectUrl = URL.createObjectURL(blob);
		try {
			triggerBlobDownload(objectUrl, resolvedName);
		} finally {
			window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
		}
		return;
	} catch {
		triggerHiddenFrameDownload(url);
	}
}

export function isTerminalPdfExportStatus(status: PdfExportJobStatus): boolean {
	return TERMINAL_STATUSES.has(status);
}

export interface PdfExportEventListener {
	onEvent: (payload: unknown) => void;
	onError: (error: unknown) => void;
}

export function listenForPdfExportEvent(
	subscribe: (handlers: PdfExportEventListener) => () => void,
	jobId: string,
	signal: AbortSignal
): Promise<PdfExportWebSocketEvent> {
	return new Promise((resolve, reject) => {
		let unsubscribe = () => {};
		const onAbort = () => {
			unsubscribe();
			reject(new DOMException('Aborted', 'AbortError'));
		};

		if (signal.aborted) {
			onAbort();
			return;
		}

		signal.addEventListener('abort', onAbort, { once: true });

		unsubscribe = subscribe({
			onEvent: raw => {
				const payload = unwrapPdfExportSyncPayload(raw);
				if (!isPdfExportWebSocketEvent(payload)) {
					return;
				}
				if (payload.jobId !== jobId) {
					return;
				}
				if (!isTerminalPdfExportStatus(payload.status)) {
					return;
				}
				unsubscribe();
				signal.removeEventListener('abort', onAbort);
				resolve(payload);
			},
			onError: error => {
				unsubscribe();
				signal.removeEventListener('abort', onAbort);
				reject(error);
			},
		});
	});
}
